import React from 'react';
import Icon from '../../../components/AppIcon';

const AttitudeAssessment = ({ 
  attitudes, 
  onAttitudeChange, 
  errors = {},
  className = '' 
}) => {
  const attitudeItems = [
    {
      key: 'jobSatisfaction',
      label: 'Job Satisfaction',
      description: 'How satisfied are you with your current role?',
      icon: 'Heart',
      color: 'text-success',
      bgColor: 'bg-success/10'
    },
    {
      key: 'stress',
      label: 'Stress Level',
      description: 'How would you rate your current stress level?',
      icon: 'Zap',
      color: 'text-warning',
      bgColor: 'bg-warning/10',
      reverse: true
    },
    {
      key: 'confidence',
      label: 'Confidence',
      description: 'How confident do you feel in your abilities?',
      icon: 'Shield',
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    },
    {
      key: 'momentum',
      label: 'Career Momentum',
      description: 'How would you rate your career progress?',
      icon: 'TrendingUp',
      color: 'text-secondary',
      bgColor: 'bg-secondary/10'
    }
  ];

  const handleSliderChange = (key, value) => {
    onAttitudeChange({
      ...attitudes,
      [key]: parseInt(value)
    });
  };

  const getSliderLabels = (reverse = false) => {
    if (reverse) {
      return { min: 'Very High', max: 'Very Low' };
    }
    return { min: 'Very Low', max: 'Very High' };
  };

  return (
    <div className={`bg-card rounded-xl border border-border shadow-sm p-8 ${className}`}>
      <div className="space-y-8">
        {/* Header */}
        <div className="text-center space-y-3">
          <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mx-auto">
            <Icon name="BarChart3" size={24} className="text-primary" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-foreground">
              Attitude Assessment
            </h3>
            <p className="text-muted-foreground mt-2">
              Rate each aspect on a scale of 1-10 to help us understand your current professional mindset
            </p>
          </div>
        </div>

        {/* Attitude Sliders */}
        <div className="space-y-8">
          {attitudeItems?.map((item) => {
            const value = attitudes?.[item?.key] || 5;
            const labels = getSliderLabels(item?.reverse);
            
            return (
              <div key={item?.key} className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${item?.bgColor}`}>
                    <Icon name={item?.icon} size={20} className={item?.color} />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-foreground">{item?.label}</h4>
                    <p className="text-sm text-muted-foreground">{item?.description}</p>
                  </div>
                  <div className="text-2xl font-bold text-primary font-mono w-12 text-center">
                    {value}
                  </div>
                </div>
                <div className="ml-13 space-y-3">
                  <div className="relative">
                    <input
                      type="range"
                      min={1}
                      max={10}
                      step={1}
                      value={value}
                      onChange={(e) => handleSliderChange(item?.key, e?.target?.value)}
                      className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer slider"
                    />
                  </div>
                  
                  <div className="flex justify-between items-center text-xs text-muted-foreground">
                    <span className="flex items-center space-x-1">
                      <span>1</span>
                      <span className="hidden sm:inline">- {labels?.min}</span>
                    </span>
                    <div className="hidden sm:flex justify-center space-x-4">
                      {[2, 3, 4, 5, 6, 7, 8, 9]?.map(num => (
                        <span key={num} className="w-4 text-center opacity-50">{num}</span>
                      ))}
                    </div>
                    <span className="flex items-center space-x-1">
                      <span className="hidden sm:inline">{labels?.max} - </span>
                      <span>10</span>
                    </span>
                  </div>
                </div>
                {errors?.[item?.key] && (
                  <div className="ml-13 flex items-center space-x-2 text-sm text-error">
                    <Icon name="AlertCircle" size={16} />
                    <span>{errors?.[item?.key]}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Summary */}
        <div className="bg-muted/50 rounded-lg p-4 space-y-2">
          <div className="flex items-center space-x-2 text-sm font-medium text-foreground">
            <Icon name="Info" size={16} />
            <span>Assessment Summary</span>
          </div>
          <div className="text-sm text-muted-foreground">
            Your responses help us create a personalized career development plan that addresses your specific needs and goals.
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttitudeAssessment;